"use client";

/**
 * StickyNavRail — unified sticky scrolltell navigator.
 *
 * Extracted from 3 drifted forks (S53):
 *   - niche-v3/StickyNavDotsNiche (supports trailing wrapped sparkle + controlled active/jump)
 *   - brand-v2/StickyBrandNav (internal IntersectionObserver, 14 scenes)
 *   - plan/StickyPlanNav (internal IO, md breakpoint — now unified to lg)
 *
 * Desktop (≥lg): fixed vertical rail on the left (dots + amber fill line).
 * Mobile (<lg):  sticky top pill with progress bar + active scene label,
 *                tap-to-expand scrollable accordion of labels.
 *
 * ─── Controlled vs uncontrolled ───
 *   Caller can either:
 *     (a) Controlled — pass `activeSceneId` + `onJump`. Parent owns scene
 *         detection (often a parent-level IntersectionObserver) and intercepts
 *         jumps (e.g. wrapped → opens an overlay rather than anchor scroll).
 *     (b) Uncontrolled — omit `activeSceneId`/`onJump`. The component runs its
 *         own IntersectionObserver + default `scrollIntoView` on click.
 *
 * ─── Section variants (kind) ───
 *   - "default"   — round dot (amber when active, white/25 when pending)
 *   - "wrapped"   — sparkle icon + thin amber separator above; signals a
 *                   trailing "finale"/"wrapped" moment. May be intercepted by
 *                   `onJump` to open an overlay instead of scrolling.
 *
 * ─── Visual preservation (S53 fixes) ───
 *   - Line height ref-measured from first-dot CENTER to last-dot CENTER, not
 *     full container — avoids line extending past the last dot or starting
 *     before the first.
 *   - Background/fill lines are z-0; dot buttons are z-10 with bg-surface —
 *     prevents the line showing through the dot circle.
 *
 * ─── Accessibility ───
 *   - nav[role=navigation] with caller-provided aria-label
 *   - aria-current="true" on active dot (both desktop + mobile)
 *   - keyboard: Tab focus per dot, Enter/Space activates
 *   - Respects useReducedMotion() (no spring, instant scroll fallback)
 *
 * ─── License ───
 *   Uses lucide-react icons (ChevronDown, Sparkles) — existing Foundry
 *   dependency. Migration to Solar icon pack can happen later without
 *   altering this component's API.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AnimatePresence,
  motion,
  useMotionValueEvent,
  useReducedMotion,
  useScroll,
} from "framer-motion";
import { ChevronDown, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";

/* ═══════════════════ Types ═══════════════════ */

export type StickyNavSectionKind = "default" | "wrapped";

export interface StickyNavSection {
  /** DOM id of the anchor target (or sentinel id if intercepted via onJump). */
  id: string;
  /** Short user-facing label shown on hover + mobile list. */
  label: string;
  /**
   * Visual variant.
   *  - "default" → round dot
   *  - "wrapped" → sparkle icon + amber separator above (finale / overlay moment)
   */
  kind?: StickyNavSectionKind;
}

interface StickyNavRailProps {
  /** Ordered list of sections. */
  sections: StickyNavSection[];
  /**
   * Controlled active section id. If omitted, the component runs its own
   * IntersectionObserver against DOM anchors matching each section.id.
   */
  activeSectionId?: string;
  /**
   * Jump handler. If omitted, default behavior is `scrollIntoView` on the
   * DOM element matching section.id. Provide a handler to intercept
   * "wrapped" → open overlay instead of scrolling.
   */
  onJump?: (id: string) => void;
  /** Required for a11y — nav element's aria-label. */
  ariaLabel: string;
  /** Unique DOM id for the mobile collapsible list (aria-controls target). */
  listId: string;
  /**
   * Optional Tailwind color class for the active dot/line accent.
   * Defaults to amber. Must be a class that plays with rgba (for the glow).
   */
  accentDotClassName?: string;
}

/* ═══════════════════ helpers ═══════════════════ */

function defaultScrollTo(id: string, reduced: boolean) {
  if (typeof window === "undefined") return;
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "start" });
}

/* ═══════════════════ Component ═══════════════════ */

export function StickyNavRail({
  sections,
  activeSectionId,
  onJump,
  ariaLabel,
  listId,
}: StickyNavRailProps) {
  const reduced = useReducedMotion();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [progress, setProgress] = useState(0);

  const isControlled = activeSectionId !== undefined;
  const [internalActiveId, setInternalActiveId] = useState<string>(
    sections[0]?.id ?? "",
  );
  const ratiosRef = useRef<Map<string, number>>(new Map());

  /* ─── Scroll progress (mobile pill bar + progressbar aria-valuenow) ─── */
  const { scrollYProgress } = useScroll();
  useMotionValueEvent(scrollYProgress, "change", (v) => {
    setProgress(Math.max(0, Math.min(1, v)));
  });

  /* ─── Uncontrolled IntersectionObserver (when no activeSectionId prop) ─── */
  useEffect(() => {
    if (isControlled) return;
    if (typeof window === "undefined" || sections.length === 0) return;

    const ratios = ratiosRef.current;
    sections.forEach((s) => ratios.set(s.id, 0));

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const id = (entry.target as HTMLElement).id;
          ratios.set(id, entry.isIntersecting ? entry.intersectionRatio : 0);
        }
        // Pick the section with the highest current intersection ratio.
        let bestId = sections[0].id;
        let bestRatio = 0;
        for (const s of sections) {
          const r = ratios.get(s.id) ?? 0;
          if (r > bestRatio) {
            bestRatio = r;
            bestId = s.id;
          }
        }
        if (bestRatio > 0) setInternalActiveId(bestId);
      },
      {
        // Active band sits ~30%–40% down the viewport.
        rootMargin: "-30% 0px -60% 0px",
        threshold: [0, 0.1, 0.25, 0.4, 0.5, 0.75, 1],
      },
    );

    const observed: Element[] = [];
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) {
        observer.observe(el);
        observed.push(el);
      }
    });

    return () => {
      observed.forEach((el) => observer.unobserve(el));
      observer.disconnect();
      ratios.clear();
    };
  }, [isControlled, sections]);

  /* ─── Resolve active index (prop wins over internal) ─── */
  const effectiveActiveId = isControlled ? activeSectionId : internalActiveId;

  const activeIndex = useMemo(() => {
    if (!effectiveActiveId) return 0;
    const idx = sections.findIndex((s) => s.id === effectiveActiveId);
    return idx < 0 ? 0 : idx;
  }, [effectiveActiveId, sections]);

  const activeSection = sections[activeIndex];
  const progressPct = Math.round(progress * 100);

  /* ─── Rail fill ratio (0..1 across dot range) ─── */
  const railFillRatio = useMemo(() => {
    if (sections.length <= 1) return 0;
    const clamped = Math.min(activeIndex, sections.length - 1);
    return clamped / (sections.length - 1);
  }, [activeIndex, sections.length]);

  /* ─── Ref-measured rail geometry (first-dot-center → last-dot-center) ─── */
  const railContainerRef = useRef<HTMLDivElement>(null);
  const [railGeom, setRailGeom] = useState<{ top: number; height: number }>({
    top: 8,
    height: 0,
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const container = railContainerRef.current;
    if (!container) return;

    const measure = () => {
      const dots = container.querySelectorAll<HTMLElement>(
        "[data-rail-dot='true']",
      );
      if (dots.length < 2) {
        setRailGeom({ top: 8, height: 0 });
        return;
      }
      const containerRect = container.getBoundingClientRect();
      const firstRect = dots[0].getBoundingClientRect();
      const lastRect = dots[dots.length - 1].getBoundingClientRect();
      const firstCenter =
        firstRect.top + firstRect.height / 2 - containerRect.top;
      const lastCenter =
        lastRect.top + lastRect.height / 2 - containerRect.top;
      setRailGeom({
        top: firstCenter,
        height: Math.max(0, lastCenter - firstCenter),
      });
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(container);
    container
      .querySelectorAll<HTMLElement>("[data-rail-dot='true']")
      .forEach((el) => ro.observe(el));
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [sections]);

  /* ─── Jump handler (caller-provided OR default scrollIntoView) ─── */
  const handleJump = useCallback(
    (id: string) => {
      if (onJump) onJump(id);
      else defaultScrollTo(id, !!reduced);
      setMobileOpen(false);
    },
    [onJump, reduced],
  );

  if (sections.length === 0) return null;

  return (
    <>
      {/* ═══ Desktop left rail (≥lg) ═══ */}
      <nav
        role="navigation"
        aria-label={ariaLabel}
        className="pointer-events-none fixed left-4 top-1/2 z-40 hidden -translate-y-1/2 lg:block xl:left-6"
      >
        <div
          ref={railContainerRef}
          className="pointer-events-auto relative flex flex-col items-center gap-y-4 py-2"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progressPct}
          aria-label={`Scroll progress: ${progressPct}%`}
        >
          {/* Background rail line — measured first→last dot center.
              z-0 so dot circles (z-10) render above. */}
          <div
            aria-hidden
            className="pointer-events-none absolute left-1/2 z-0 w-px -translate-x-1/2 bg-white/10"
            style={{
              top: `${railGeom.top}px`,
              height: `${railGeom.height}px`,
            }}
          />
          {/* Amber progress fill */}
          <div
            aria-hidden
            className="pointer-events-none absolute left-1/2 z-0 w-px -translate-x-1/2 bg-amber-400/70"
            style={{
              top: `${railGeom.top}px`,
              height: `${railGeom.height * railFillRatio}px`,
              transition: reduced ? "none" : "height 300ms ease-out",
            }}
          />

          {sections.map((section, idx) => {
            const isActive = idx === activeIndex;
            const isPast = idx < activeIndex;
            const isWrapped = section.kind === "wrapped";
            return (
              <DotButton
                key={section.id}
                section={section}
                idx={idx}
                total={sections.length}
                isActive={isActive}
                isPast={isPast}
                isWrapped={isWrapped}
                reduced={!!reduced}
                onClick={() => handleJump(section.id)}
              />
            );
          })}
        </div>
      </nav>

      {/* ═══ Mobile bottom pill (<lg) ═══
       * Positioned at bottom — thumb-reachable and doesn't overlap
       * scene hero copy at the top of each scroll-tell view.
       *
       * `var(--niche-sticky-cta-h, 0px)` lifts the pill above any active
       * sticky CTA bar (DecideScene Phase 2). The var is published on
       * <html> by DecideScene only — Plan/Brand/Landing don't set it, so
       * the fallback `0px` keeps the original bottom on those pages.
       * `transition-[bottom]` smooths the lift/drop when the CTA arms or
       * disarms so the pill doesn't jump under the user's thumb. */}
      <nav
        role="navigation"
        aria-label={ariaLabel}
        className="fixed bottom-[calc(1rem+env(safe-area-inset-bottom)+var(--niche-sticky-cta-h,0px))] left-1/2 z-[60] -translate-x-1/2 transition-[bottom] duration-200 ease-out lg:hidden"
      >
        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          aria-expanded={mobileOpen}
          aria-controls={listId}
          aria-label={`${ariaLabel} — currently on ${activeSection?.label ?? "first"}, ${activeIndex + 1} of ${sections.length}. Tap to ${mobileOpen ? "close" : "open"} list.`}
          className={cn(
            "flex items-center gap-3 rounded-full border bg-surface/85 px-4 py-2 shadow-lg shadow-black/30 backdrop-blur-md max-[430px]:gap-2 max-[430px]:px-3",
            "text-[11px] text-white/90 transition-colors hover:text-white",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/40 focus-visible:ring-offset-2 focus-visible:ring-offset-surface",
            mobileOpen
              ? "border-amber-300/25 shadow-[0_0_28px_-18px_rgba(251,191,36,0.9)]"
              : "border-subtle",
          )}
        >
          <span
            aria-hidden
            className="relative block h-1 w-24 overflow-hidden rounded-full bg-white/10 max-[430px]:w-16"
          >
            <span
              className="absolute inset-y-0 left-0 rounded-full bg-amber-400"
              style={{
                width: `${progressPct}%`,
                transition: reduced ? "none" : "width 200ms linear",
              }}
            />
          </span>
          <span
            aria-hidden
            className="tabular-nums tracking-wider text-white/90"
          >
            {activeIndex + 1}/{sections.length}
          </span>
          <span
            aria-hidden
            className="max-w-[120px] truncate font-medium text-white/80 max-[430px]:max-w-[74px]"
          >
            {activeSection?.label ?? ""}
          </span>
          <ChevronDown
            className={cn(
              "size-3.5 text-white/50",
              !reduced && "transition-transform",
              mobileOpen && "rotate-180",
            )}
            aria-hidden
          />
        </button>

        <AnimatePresence>
          {mobileOpen && (
            <motion.ul
              id={listId}
              role="list"
              initial={reduced ? { opacity: 0 } : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduced ? { opacity: 0 } : { opacity: 0, y: 8 }}
              transition={{ duration: reduced ? 0 : 0.18, ease: "easeOut" }}
              className={cn(
                "absolute bottom-full left-1/2 mb-3 max-h-[62vh] w-[calc(100vw-32px)] max-w-sm -translate-x-1/2 overflow-y-auto rounded-2xl border border-amber-300/15 bg-surface-card/95 p-2 shadow-2xl shadow-black/50 ring-1 ring-inset ring-white/[0.04] backdrop-blur-md",
              )}
            >
              <li aria-hidden className="px-3 pb-2 pt-2">
                <div className="flex items-center justify-between gap-4">
                  <span className="font-heading text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-300/70">
                    Chapters
                  </span>
                  <span className="font-mono text-[10px] tabular-nums text-white/45">
                    {progressPct}%
                  </span>
                </div>
                <div className="mt-2 h-px bg-white/10" />
              </li>
              {sections.map((section, idx) => {
                const isActive = idx === activeIndex;
                const isWrapped = section.kind === "wrapped";
                return (
                  <li key={section.id}>
                    <button
                      type="button"
                      onClick={() => handleJump(section.id)}
                      aria-current={isActive ? "true" : undefined}
                      aria-label={`Go to ${section.label}, ${idx + 1} of ${sections.length}`}
                      className={cn(
                        "flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left text-sm transition-colors",
                        "focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/40",
                        isActive
                          ? "border-amber-300/20 bg-amber-400/10 text-amber-200"
                          : "border-transparent text-white/70 hover:bg-white/[0.05] hover:text-white",
                      )}
                    >
                      <span
                        className={cn(
                          "inline-flex size-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold tabular-nums",
                          isWrapped
                            ? "bg-amber-400/20 text-amber-200"
                            : isActive
                              ? "bg-amber-400 text-black"
                              : "bg-white/10 text-white/60",
                        )}
                      >
                        {isWrapped ? (
                          <Sparkles className="size-3" aria-hidden />
                        ) : (
                          idx + 1
                        )}
                      </span>
                      <span className="truncate">{section.label}</span>
                    </button>
                  </li>
                );
              })}
            </motion.ul>
          )}
        </AnimatePresence>
      </nav>
    </>
  );
}

/* ═══════════════════ internals ═══════════════════ */

interface DotButtonProps {
  section: StickyNavSection;
  idx: number;
  total: number;
  isActive: boolean;
  isPast: boolean;
  isWrapped: boolean;
  reduced: boolean;
  onClick: () => void;
}

function DotButton({
  section,
  idx,
  total,
  isActive,
  isPast,
  isWrapped,
  reduced,
  onClick,
}: DotButtonProps) {
  const [hover, setHover] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick();
    }
  };

  // Visual separation for wrapped — a thin amber divider above the sparkle.
  const showSeparator = isWrapped;

  return (
    <div
      data-rail-dot="true"
      className={cn(
        "group relative z-10 flex items-center",
        showSeparator && "mt-4 pt-4",
      )}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {showSeparator && (
        <span
          aria-hidden
          className="pointer-events-none absolute left-1/2 top-0 z-10 h-2 w-px -translate-x-1/2 bg-amber-400/40"
        />
      )}
      <button
        ref={btnRef}
        type="button"
        onClick={onClick}
        onKeyDown={handleKeyDown}
        onFocus={() => setHover(true)}
        onBlur={() => setHover(false)}
        aria-label={`Go to ${section.label} (${idx + 1} of ${total})`}
        aria-current={isActive ? "true" : undefined}
        className={cn(
          "relative z-10 flex size-5 items-center justify-center rounded-full bg-surface",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/40 focus-visible:ring-offset-2 focus-visible:ring-offset-surface",
        )}
      >
        {isWrapped ? (
          <Sparkles
            className={cn(
              "size-3.5 transition-colors",
              isActive ? "text-amber-300" : "text-amber-400/70",
            )}
            aria-hidden
          />
        ) : (
          <motion.span
            aria-hidden
            initial={false}
            animate={
              reduced
                ? { scale: 1 }
                : { scale: isActive ? 1.4 : hover ? 1.2 : 1 }
            }
            transition={{ type: "spring", stiffness: 300, damping: 22 }}
            className={cn(
              "block h-2.5 w-2.5 rounded-full transition-colors",
              isActive
                ? "bg-amber-400 shadow-[0_0_10px_rgba(251,191,36,0.6)]"
                : isPast
                  ? "bg-amber-400/50"
                  : "bg-white/25 group-hover:bg-white/60",
            )}
          />
        )}
        {/* Active progress ring (desktop only — looks too heavy on mobile) */}
        {isActive && !isWrapped && !reduced && (
          <span
            aria-hidden
            className="pointer-events-none absolute inset-[-4px] rounded-full ring-1 ring-amber-400/40"
          />
        )}
      </button>

      <AnimatePresence>
        {hover && (
          <motion.span
            aria-hidden
            initial={reduced ? { opacity: 0 } : { opacity: 0, x: -4 }}
            animate={{ opacity: 1, x: 0 }}
            exit={reduced ? { opacity: 0 } : { opacity: 0, x: -4 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            className={cn(
              "pointer-events-none absolute left-full ml-3 whitespace-nowrap rounded-full border border-subtle bg-surface-card px-3 py-1.5",
              "text-[11px] font-medium tracking-wide text-white/85 shadow-lg shadow-black/30",
            )}
          >
            {section.label}
          </motion.span>
        )}
      </AnimatePresence>
    </div>
  );
}
